<script lang="ts">
	import clickOutside from '$lib/actions/clickOutside';
	import TextInput, { type FormState } from '$lib/input/textInput.svelte';
	import Mill from '$lib/logos/mill.svelte';
	import Blok from '$lib/storyblok/blok.svelte';
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import { editable } from '../../editable';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { identify } from '$lib/segment/client';
	import { fade, fly } from 'svelte/transition';
	import Checkmark from '$lib/icons/checkmark.svelte';
	import { page } from '$app/state';
	import { enhance } from '$app/forms';
	import type { ActionResult } from '@sveltejs/kit';
	import { event } from '$lib/utils/tracking';
	import type { ChewieUMCouponIdentifier } from '$lib/api/chewieUM.types';
	import { checkoutSession } from '$lib/state/checkoutSession.svelte';

	interface Props {
		content: ISbStoryData<{
			asset: ISbStoryData[];
			headline: ISbRichtext;
			subline: string;
			legal_terms: string;
			success_headline: string;
			success_subline: string;
			success_button: ISbStoryData[];
			coupon_identifier: ChewieUMCouponIdentifier;
			coupon_info_text: string;
		}>['content'];
		city: string;
		onclose: () => void;
	}

	let { content, onclose, city }: Props = $props();
	let formState: FormState = $state(null);
	let error = $state<string | null>(null);

	let step: 'capture' | 'success' = $state('capture');
	let headlineWithCity: undefined | ISbRichtext = $state(undefined);
	let showToast = $state(false);

	function replaceCity(city: string) {
		const copy = structuredClone(content.headline);
		copy.content?.forEach((t) => {
			t.content?.forEach((t) => {
				t.text = t.text?.replace('[city]', city);
			});
		});
		headlineWithCity = copy;
	}

	const handleEnhance = () => {
		formState = 'processing';

		return async (response: {
			result: ActionResult<{
				email: string;
				couponCode?: string;
				emailVerificationResult?: string;
				couponValue?: number;
			}>;
		}) => {
			const { result } = response;

			if (result.type === 'failure') {
				formState = 'error';
				error = result.data?.message || null;
				return;
			}

			if (result.type !== 'success') {
				return;
			}

			if (result.data?.couponCode) {
				await checkoutSession.applyCoupon(result.data.couponCode);
			}

			if (result.data) {
				step = 'success';

				await identify({ email: result.data.email });
				await event('Email Captured', {
					email: result.data.email,
					emailSource: 'modal',
					emailVerificationResult: result.data.emailVerificationResult,
					referringPage: window.location.pathname
				});
			}
		};
	};

	$effect(() => {
		if (step === 'success') {
			showToast = true;
			setTimeout(() => (showToast = false), 3000);
		}
	});

	$effect(() => {
		replaceCity(city);
	});
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<!-- svelte-ignore a11y_click_events_have_key_events -->

<div
	class="relative h-full w-full overflow-hidden bg-white lg:rounded-xl"
	use:editable={content}
	use:clickOutside={onclose}
>
	<div class="absolute left-5 top-4 lg:hidden">
		<a href="/">
			<Mill class="h-8 text-green-3" />
		</a>
	</div>

	<PageGrid16 margin gridClass="lg:h-full" class="lg:h-full">
		{#if step === 'capture'}
			<div class="w-full overflow-hidden max-lg:hidden lg:absolute lg:inset-y-0 lg:left-0 lg:w-1/2">
				<Blok content={content.asset[0]} />
			</div>
			<div
				class="relative col-span-full flex h-full flex-col max-lg:mt-22 lg:col-start-10 lg:justify-center"
			>
				<a class="absolute left-0 top-25 max-lg:hidden" href="/">
					<Mill class="h-8 text-green-3" />
				</a>
				<div
					class="headline text-[36px]/[120%] tracking-[-.36px] lg:text-[48px]/[120%] lg:tracking-[-.48px]"
				>
					{#if headlineWithCity}
						<SbRichtext text={headlineWithCity} />
					{/if}
				</div>

				<p class="mt-3 text-[14px]/[160%] lg:mt-4 lg:text-[16px]/[150%]">
					{content.subline}
				</p>

				<form
					method="POST"
					action="/?/emailCapture"
					use:enhance={handleEnhance}
					class="mt-6 md:max-w-[440px]"
				>
					<input type="hidden" name="couponIdentifier" value={content.coupon_identifier} />
					<input type="hidden" name="couponInfoText" value={content.coupon_info_text} />
					<input type="hidden" name="pathname" value={page.url.pathname} />

					<TextInput
						{formState}
						name="email"
						placeholder="Enter your email address"
						withSubmitButton
						autocomplete="email"
						autofocus
					/>

					{#if error}
						<p class="mt-2 text-[12px]/[100%] text-red-500">{error}</p>
					{/if}
				</form>

				<p class="mt-4 text-[12px]/[160%] text-black/[.4] lg:absolute lg:bottom-11">
					{content.legal_terms}
				</p>
			</div>
		{:else if step === 'success'}
			<div
				class="col-span-full overflow-hidden max-lg:mt-22 max-lg:aspect-3/2 max-lg:rounded-xl lg:absolute lg:inset-y-0 lg:left-0 lg:w-1/2"
			>
				<Blok content={content.asset[0]} />
			</div>
			<div
				in:fade
				class="relative col-span-full flex h-full flex-col justify-center max-lg:text-center lg:col-start-10 lg:col-end-[15]"
			>
				<a class="absolute left-0 top-25 max-lg:hidden" href="/">
					<Mill class="h-8 text-green-3" />
				</a>
				<p
					class="mt-7 text-[36px]/[120%] tracking-[-.36px] lg:text-[48px]/[120%] lg:tracking-[-.48px]"
				>
					{content.success_headline}
				</p>

				<p class="mt-2 text-pretty text-[14px]/[160%] lg:mt-4 lg:text-[16px]/[150%]">
					{content.success_subline}
				</p>
				{#if content.success_button[0]}
					<div class="mt-6 flex justify-center lg:justify-start">
						<Blok content={content.success_button[0]} />
					</div>
				{/if}
			</div>
		{/if}
	</PageGrid16>
</div>

{#if showToast}
	<div
		transition:fly={{ y: '200%' }}
		class="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-x-2 whitespace-nowrap rounded-full bg-gray-2 py-3 pl-4 pr-5 text-white lg:bottom-17"
	>
		<Checkmark class="h-4 w-4" />
		<p class="text-nowrap">Code applied in your cart</p>
	</div>
{/if}

<style>
	:global(.headline .highlight) {
		background-color: #ffe8c8;
		border-radius: 8px;
		padding-left: 4px;
		padding-right: 4px;
		white-space: nowrap;
	}
</style>
